import React, { PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  Fade,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useSelector } from 'react-redux'
import { Quote } from '../../storage/types'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import {
  OrderLocationButton,
  PadButton,
  WrapperA,
  BoldWrapper,
  PadIcon,
  PadContent,
} from '../../../../components/Pad/Pad'
import {
  TermsAndConditionsText,
  FulfilmentFAQsText,
} from '../../../../components/layout/TermsAndConditionsText'
import TestIds from '../../../../testIds'
import { URLS } from '../../../../util/consts'
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage'
import { colors } from '../../../../themes/colors'
import {
  Container,
  ReferenceNoteWrapper,
  Textarea,
  ButtonBox,
  GapContainer,
  InlineFlex,
} from '../../../cart/components/CartSummaryComponents'
import { isQuoteStatusRequested } from '../../utils/getQuoteStatus'
import { selectStoreById } from '../../../stores/storage/stores.slice'
import { CartMessage } from '../../../cart/utils/cartMessages'
import { CartDeliveryMode } from '../../../cart/storage/cart.types'
import { usePickUpStoreComponents } from '../../../cart/components/PickUpStoreModal'
import Modal from '../../../../components/Modal'
import { cloneDeep } from 'lodash'

interface QuoteSummaryProps extends PropsWithChildren {
  quote: Quote
  setQuote: (quote: Quote) => void
  note: string | null
  setNote: (note: string) => void
  handleContinue: () => void
}

export const QuoteSummary = ({
  quote,
  setQuote,
  handleContinue,
  note,
  setNote,
  children,
}: QuoteSummaryProps) => {
  const currentAccount = useSelector(selectCurrentFarmerAccount)
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))
  const elRef = useRef<HTMLElement>(null)

  const pickupStore = useSelector(selectStoreById(quote.delivery.pickupStoreId ?? undefined))

  const { container, footer } = usePickUpStoreComponents(
    {
      cartDeliveryMode: pickupStore ? CartDeliveryMode.PICKUP : CartDeliveryMode.DELIVERY,
      deliveryDetails: quote.delivery.deliveryAddress ?? undefined,
      pickupStore,
      addressSettings: {},
    },
    (state) => {
      if (state.pickupStore) {
        quote.delivery.pickupStoreId = state.pickupStore.inventorySiteId
        quote.delivery.deliveryAddress = null
        quote.delivery.deliveryInstructions = null
        quote.delivery.dairySupplyNumber = null

        setQuote(cloneDeep(quote))
      }
    },
    (state) => {
      quote.delivery.pickupStoreId = null
      const deliveryDetails = state.delivery.deliveryDetails
      quote.delivery.deliveryAddress = {
        streetAddress: deliveryDetails.streetAddress,
        name: deliveryDetails.addressName,
        city: deliveryDetails.city,
        postcode: deliveryDetails.postCode,
        recordId: deliveryDetails.recordId,
      }
      quote.delivery.deliveryInstructions = deliveryDetails.deliveryInstructions ?? null
      quote.delivery.dairySupplyNumber = deliveryDetails.dairySupplyNumber ?? null
      quote.delivery.isUrgent = deliveryDetails.urgent ?? null
      setQuote(cloneDeep(quote))
    },
    () => {
      setIsModalOpen(false)
    },
  )

  const [isNotesOpen, setIsNotesOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isErrorOnPickupStore, setIsErrorOnPickupStore] = React.useState<boolean>(false)
  const [buttonBoxHeight] = useState(80)

  const renderEditPickUpStoreButton = React.useMemo(() => {
    const handleOpenPickUpStoreModal = () => {
      setIsModalOpen(true)
    }
    if (pickupStore)
      return (
        <OrderLocationButton
          onClick={handleOpenPickUpStoreModal}
          buttonTestId={TestIds.feat.quotes.summary.pickUpButton}
          icon='location_on'
          title='Pickup'
          subtitle={pickupStore.orgUnitName}
          isEditEnabled
        />
      )
    if (quote.delivery.deliveryAddress) {
      const address = quote.delivery.deliveryAddress
      const subtitle = `${address.streetAddress} ${address.city} ${address.postcode}`
      return (
        <OrderLocationButton
          onClick={handleOpenPickUpStoreModal}
          buttonTestId={TestIds.feat.quotes.summary.pickUpButton}
          icon='local_shipping'
          title='Delivery'
          subtitle={subtitle}
          isEditEnabled
        />
      )
    }
    return (
      <PadButton
        shade='light'
        sx={{ justifyContent: 'flex-start', p: 1 }}
        onClick={handleOpenPickUpStoreModal}
        data-testid={TestIds.feat.cart.summary.pickUpButton}
        aria-invalid={isErrorOnPickupStore}
        aria-errormessage={isErrorOnPickupStore ? 'pickup-error' : undefined}
        errorMessage={isErrorOnPickupStore ? 'Pickup store required' : undefined}
      >
        <WrapperA>
          <BoldWrapper>
            <PadIcon className='material-symbols-outlined'>add</PadIcon>
            <PadContent>Select pickup store or delivery</PadContent>
          </BoldWrapper>
        </WrapperA>
      </PadButton>
    )
  }, [container, isErrorOnPickupStore, setIsModalOpen])

  const scrollToTop = () => {
    setTimeout(() => {
      const mc = document.getElementById('main-scroll-container')
      mc?.scrollTo({
        behavior: 'smooth',
        top: mc.scrollHeight + 500,
        left: 0,
      })
    }, 100)
  }

  const handleAddNotes = useCallback(() => {
    setIsNotesOpen(true)
    scrollToTop()
  }, [setIsNotesOpen])

  const renderAddNotes = useMemo(() => {
    const showButton = !isNotesOpen

    if (showButton)
      return (
        <PadButton
          shade='light'
          sx={{
            justifyContent: 'flex-start',
            p: 1,
          }}
          onClick={handleAddNotes}
          data-testid={TestIds.feat.quotes.summary.addNotesButton}
        >
          <WrapperA>
            <BoldWrapper>
              <PadIcon className='material-symbols-outlined'>add</PadIcon>
              <PadContent>Add customer notes</PadContent>
            </BoldWrapper>
          </WrapperA>
        </PadButton>
      )
    return (
      <Fade in={!showButton}>
        <ReferenceNoteWrapper>
          <Textarea
            aria-label='Customer facing note for quote'
            label='Customer facing notes:'
            value={note ?? ''}
            onChange={(event) => {
              if (event.target.value.length <= 200 && currentAccount) setNote(event.target.value)
            }}
            multiline
            inputProps={{ maxLength: 200 }}
            minRows={4}
            variant='outlined'
            helperText={`${note ? note.length : 0}/200 characters`}
            testId={TestIds.feat.quotes.summary.notesField}
          />
        </ReferenceNoteWrapper>
      </Fade>
    )
  }, [note, setNote, isNotesOpen])

  const onContinue = () => {
    if (!pickupStore && !quote.delivery.deliveryAddress) {
      setIsErrorOnPickupStore(true)
    } else {
      setIsErrorOnPickupStore(false)
      handleContinue()
    }
  }

  const submitOrLoading = useMemo(() => {
    if (quote && currentAccount) {
      return (
        <ButtonBox ref={elRef}>
          <Button
            variant='contained'
            sx={{ width: '100%' }}
            onClick={onContinue}
            data-testid={TestIds.feat.quotes.summary.continueButton}
          >
            Continue to checkout
          </Button>
        </ButtonBox>
      )
    }
  }, [quote, currentAccount, elRef, onContinue])

  const userCanInteractWitQuote = useMemo(() => {
    return !isQuoteStatusRequested(quote)
  }, [quote.status])

  return (
    <Container buttonBoxHeight={buttonBoxHeight}>
      <GapContainer gap='1rem'>
        {children}
        {userCanInteractWitQuote && (
          <>
            <InlineFlex>
              <Box component='span'>
                <Typography
                  variant='body2'
                  data-testid={TestIds.feat.quotes.summary.orderChargedMessage}
                >
                  {CartMessage.chargedToAccount}
                </Typography>
              </Box>
            </InlineFlex>
            <a href={URLS.termsAndConditions} target='_blank' rel='noreferrer'>
              <Stack direction='row' alignItems='center' spacing={1}>
                <MaterialSymbol sx={{ color: colors.primary500 }}>Description</MaterialSymbol>
                <TermsAndConditionsText />
              </Stack>
            </a>
            <a href={URLS.fulfilmentFAQs} target='_blank' rel='noreferrer'>
              <Stack direction='row' alignItems='center' spacing={1}>
                <MaterialSymbol sx={{ color: colors.primary500 }}>Description</MaterialSymbol>
                <FulfilmentFAQsText />
              </Stack>
            </a>
          </>
        )}
      </GapContainer>

      {userCanInteractWitQuote && (
        <GapContainer gap='1rem'>
          <GapContainer>{renderEditPickUpStoreButton}</GapContainer>
          <GapContainer>{renderAddNotes}</GapContainer>

          {!isSm && <Divider color={colors.primary200} sx={{ ml: -2, mr: -2 }} />}
          {submitOrLoading}
        </GapContainer>
      )}

      <Modal
        title='Pickup or delivery?'
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        footer={footer}
        testId={TestIds.feat.pickUpStore.modal}
        maxWidth='md'
        paperProps={{
          sx: { height: isSm ? 'inherit' : '844px' },
        }}
      >
        {container}
      </Modal>
    </Container>
  )
}

export default QuoteSummary
